import type { TFunction } from 'i18next';
import { formatDate } from 'utils/formatDate';
import { calendarDay } from 'utils/interventionDate';
import type { AdminIssue } from 'api/adminIssuesApi';
import type { ReferentialTag } from 'api/techniciansApi';
import { REFUSAL_REASON_CODES } from 'api/interventionRequestsApi';
import type {
  InterventionCandidate,
  InterventionRequest,
  InterventionRequestStatus,
  MissionAssignment,
  RefusalReasonCode,
  UnresolvedSkill,
} from 'api/interventionRequestsApi';

/**
 * The vocabulary of the « À venir » tab: what a mission's assignment *is*, what it reads as, and
 * what the summary line counts. Words and arithmetic only — no markup, so the row, the drawer and
 * the summary never disagree on what `pending` means.
 */

/** One row: the Jira mission, plus what Postgres knows about its assignment. */
export interface UpcomingMission extends AdminIssue {
  specialties: ReferentialTag[];
  /** Read-only, entered in Jira (D66-i). Empty is a mission with none, never a failure. */
  expertises: ReferentialTag[];
  /** Codes Jira carries that the referential does not hold — the drawer says which and what to do. */
  unresolvedSkills: UnresolvedSkill[];
  /** The newest request, whatever its status. Null when none was ever sent. */
  request: InterventionRequest | null;
  /** The store's postal code, read off the ticket. Null when the ticket does not carry one. */
  storePostalCode: string | null;
  /** Live solicitations — pending or accepted. 0 when the assignments were never read. */
  liveRequestCount: number;
}

/**
 * The four states a row can be in — and there is **no « Expirée »** (D5): the status was dropped,
 * there is no expiry job, and a countdown would promise one.
 *
 * `cancelled` and `superseded` collapse into `unassigned`, because that is what they are on this
 * screen: nobody is being asked. `superseded` is never written in V1 anyway; it is handled here so
 * the day the race to acceptance arrives this file is not the one that renders a blank cell.
 */
export type AssignmentState = 'unassigned' | 'pending' | 'accepted' | 'declined';

/**
 * The intervention date as a calendar day in the reader's locale, or null when the ticket has
 * none — **and never a time** (D15): the desk field can carry one, nothing carries a slot, so
 * printing one would invent it. Local midnight off the written day, since `new Date('2026-07-30')`
 * is UTC and would shift the day west of Greenwich. Unparseable reads as no date.
 */
export const interventionDay = (value: string | null): string | null => {
  // Through `calendarDay` and not a slice of its own: two readers of the same Jira field must not
  // disagree. `2026-02-31` used to print « 3 mars 2026 » here while the ranking read no day at
  // all — the send was unblocked by a date the filter could not see (L5f).
  const day = calendarDay(value);
  return day === null ? null : formatDate(`${day}T00:00:00`);
};

/** From a bare status — what a candidate card carries about a technician already asked. */
export const statusState = (status: InterventionRequestStatus | null): AssignmentState => {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'accepted':
      return 'accepted';
    case 'declined':
      return 'declined';
    default:
      return 'unassigned';
  }
};

export const assignmentState = (request: InterventionRequest | null): AssignmentState =>
  statusState(request?.status ?? null);

/**
 * A `switch` of string literals, never a key built by concatenating the state onto a prefix: the
 * repo's static guard requires a literal, and a concatenated key is invisible to the parity check.
 *
 * « Refusé — à relancer » and not just « Refusé »: after a refusal the mission has no live request
 * at all, so the sentence has to say that somebody must act, which is the whole compensation for
 * sending sequentially with no expiry.
 */
export const assignmentLabel = (t: TFunction, state: AssignmentState): string => {
  switch (state) {
    case 'pending':
      return t('Awaiting an answer');
    case 'accepted':
      // « Pourvue » and not « Technicien confirmé »: the subject of this column is the mission,
      // and it is the word the rest of the L5b vocabulary already uses.
      return t('Filled');
    case 'declined':
      return t('Declined — ask someone else');
    default:
      return t('No technician');
  }
};

/**
 * Same rule as `assignmentLabel`: a `switch` of string literals, never a key built by
 * concatenating the code onto a prefix — the repo's static guard requires a literal, and a
 * concatenated key is invisible to the parity check.
 */
export const refusalReasonLabel = (t: TFunction, code: RefusalReasonCode): string => {
  switch (code) {
    case 'already_booked':
      return t('Already booked on that slot');
    case 'too_far':
      return t('Store too far away');
    case 'out_of_skills':
      return t('Outside my skills');
    case 'unavailable':
      return t('Unavailable on that date');
    case 'other':
      return t('Other reason');
  }
};

/**
 * What a refusal reads as: the code's label, the free precision, or both. Legacy refusals carry
 * text and no code — the migration rewrote nothing — so either half alone is a valid answer.
 *
 * `null` rather than `''` for "nothing to say": it is what lets the callers drop their ` — `
 * separator instead of rendering a dangling dash.
 */
/**
 * The label of a code **only when the code is one we know**. An unexpected value off the wire —
 * a new code deployed on the API before the screen — reads as nothing rather than as a guessed
 * label, and every surface that renders a refusal goes through this rather than re-deriving it.
 */
export const knownRefusalReasonLabel = (
  t: TFunction,
  code: RefusalReasonCode | null
): string | null => (code && REFUSAL_REASON_CODES.includes(code) ? refusalReasonLabel(t, code) : null);

export const refusalDetail = (t: TFunction, request: InterventionRequest | null): string | null => {
  if (request?.status !== 'declined') return null;
  const label = knownRefusalReasonLabel(t, request.refusalReasonCode);
  const text = request.refusalReason?.trim() || null;
  return [label, text].filter(Boolean).join(' · ') || null;
};

/**
 * Emerald for confirmed, amber for a wait, rose for a refusal, zinc for nobody — the same mapping
 * as `AvailabilityBadge`, so amber means "waiting on somebody" on both sides of the app.
 */
export const ASSIGNMENT_TONE: Record<AssignmentState, string> = {
  accepted: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  declined: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  unassigned: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
};

export const ASSIGNMENT_DOT: Record<AssignmentState, string> = {
  accepted: 'bg-emerald-500',
  pending: 'bg-amber-500',
  declined: 'bg-rose-500',
  unassigned: 'bg-zinc-400',
};

/** The technician's name as the row shows it, falling back to the sign-in identifier. */
export const technicianName = (request: InterventionRequest): string => {
  const { firstName, lastName, username } = request.technician;
  return [firstName, lastName].filter(Boolean).join(' ') || username;
};

/**
 * « 2 sollicités · en attente » — what the Technician cell reads when several technicians were
 * asked and none has accepted yet. `null` the rest of the time, which is what keeps the caller on
 * its usual avatar-and-name branch.
 *
 * Naming one of several pending technicians read as « he is on it », which is exactly what is not
 * settled. An acceptance names its technician whatever the count: that one is a fact.
 *
 * Takes the two facts rather than a mission, so the follow-up screen runs **this** rule instead of
 * its own: the two screens disagreed about the same mission until D54.
 */
export const pendingSolicitationsLabel = (
  t: TFunction,
  status: InterventionRequest['status'] | null | undefined,
  liveRequestCount: number
): string | null =>
  status !== 'accepted' && liveRequestCount >= 2
    ? t('{{count}} solicited · awaiting an answer', { count: liveRequestCount })
    : null;

/**
 * The best candidate nobody has tried yet — what the mock-up calls `nextTech`, and what its
 * « Proposer au suivant » button sends to.
 *
 * A **read**, not a server-side decision: the API returns the candidates sorted by score, each
 * carrying what it already answered on this mission, and the first one without an answer is the
 * next to try. Already-solicited technicians stay in the list, greyed rather than hidden, which
 * is what makes this pick legible instead of magical.
 *
 * `available` as well as untried, exactly as the mock-up's own filter reads
 * (`!r.tried && r.avail === 'available'`): the score already pushes an unavailable technician down
 * by 900, but a one-click action must not quietly pick one — for him the answer is "no" before it
 * is asked. Nobody left means the button says so rather than sending.
 */
export const nextToTry = (candidates: InterventionCandidate[]): InterventionCandidate | null =>
  candidates.find((candidate) => candidate.lastRequestStatus === null && candidate.available) ?? null;

export interface AssignmentSummary {
  planned: number;
  unassigned: number;
  pending: number;
  accepted: number;
}

/**
 * The summary line's four figures, **over the rows on screen**.
 *
 * They are not scaled to the whole filter, and the line says so: the mission count comes from
 * Jira and the assignment states from Postgres, and no single query counts Jira missions by a
 * status that lives in another database. The table footer already gives the filter's total — the
 * line restricts itself rather than contradicting it.
 */
export const summarize = (missions: UpcomingMission[]): AssignmentSummary => {
  const summary: AssignmentSummary = { planned: missions.length, unassigned: 0, pending: 0, accepted: 0 };
  for (const mission of missions) {
    const state = assignmentState(mission.request);
    // A refusal leaves the mission with nobody, so it counts as unassigned — the figure answers
    // "how many still need a technician", which is the only question this line is asked.
    if (state === 'pending') summary.pending += 1;
    else if (state === 'accepted') summary.accepted += 1;
    else summary.unassigned += 1;
  }
  return summary;
};

/**
 * The Jira listing and the assignment read are two calls, joined here by key.
 *
 * A mission absent from the assignments carries nothing at all — the endpoint leaves it out on
 * purpose — so the defaults are an empty list and nulls, never a placeholder row.
 */
export const mergeAssignments = (
  issues: AdminIssue[],
  assignments: MissionAssignment[] | undefined
): UpcomingMission[] => {
  const byIssue = new Map((assignments ?? []).map((assignment) => [assignment.issueKey, assignment]));
  return issues.map((issue) => ({
    ...issue,
    specialties: byIssue.get(issue.issueKey)?.specialties ?? [],
    expertises: byIssue.get(issue.issueKey)?.expertises ?? [],
    unresolvedSkills: byIssue.get(issue.issueKey)?.unresolvedSkills ?? [],
    request: byIssue.get(issue.issueKey)?.request ?? null,
    storePostalCode: byIssue.get(issue.issueKey)?.storePostalCode ?? null,
    liveRequestCount: byIssue.get(issue.issueKey)?.liveRequestCount ?? 0,
  }));
};
